import {
  buildDuplicationEntry,
  fromEmbeddedChild,
  hasDuplicateOf,
  isDuplicateNameAvailable as isNameAvailable,
  parentKindOf,
} from "../utils/duplicationUtils";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  columnDescriptorsToColumnDefinitions,
  useLazyGetColumnsForImmediateReportQuery,
  useLazyGetColumnsForReportQuery,
  useLazyGetColumnsForTablesQuery,
} from "services/reportsV3Service";

const EMPTY_STATE = { duplications: [], duplicationColumns: {} };

// The locally held duplicate source reports of the builder: the list itself,
// the duplication the dialog is naming, and each entry's columns in the
// shape getColumnsForReport returns. The back-end is only ever asked about a
// duplication's parent, never about the duplication.
export const useDuplications = ({ tables, savedReports }) => {
  const [state, setState] = useState(EMPTY_STATE);
  const [pendingDuplication, setPendingDuplication] = useState(null);
  const columnsRef = useRef(state.duplicationColumns);
  columnsRef.current = state.duplicationColumns;

  const [fetchColumnsForTable] = useLazyGetColumnsForTablesQuery();
  const [fetchColumnsForReport] = useLazyGetColumnsForReportQuery();
  const [
    fetchColumnsForImmediateReport,
  ] = useLazyGetColumnsForImmediateReportQuery();

  const fetchParentColumns = useCallback(
    (parent) =>
      parent.kind === "table"
        ? fetchColumnsForTable([parent.internalName]).unwrap()
        : fetchColumnsForReport(parent.internalName, true).unwrap(),
    [fetchColumnsForTable, fetchColumnsForReport]
  );

  const parentOf = useCallback(
    (entry) => {
      const kind = parentKindOf(entry);
      const sources = (kind === "table" ? tables : savedReports) ?? [];
      const match = sources.find((s) => s.internalName === entry.duplicateOf);
      return {
        kind,
        internalName: entry.duplicateOf,
        displayName: match?.displayName || entry.duplicateOf,
      };
    },
    [tables, savedReports]
  );

  const beginDuplication = useCallback(
    async (parent) => {
      const columns = await fetchParentColumns(parent);
      setPendingDuplication({ parent, columns });
    },
    [fetchParentColumns]
  );

  const beginEditDuplication = useCallback(
    async (entry) => {
      const parent = parentOf(entry);
      const columns = await fetchParentColumns(parent);
      setPendingDuplication({ parent, columns, entry });
    },
    [parentOf, fetchParentColumns]
  );

  const cancelDuplication = useCallback(() => setPendingDuplication(null), []);

  const fetchDuplicationColumns = useCallback(
    async (entry) => {
      const cached = columnsRef.current[entry.internalName];
      if (cached) return cached;
      const columns =
        parentKindOf(entry) === "report"
          ? await fetchColumnsForReport(entry.duplicateOf, true).unwrap()
          : await fetchColumnsForImmediateReport(entry).unwrap();
      setState((prev) => ({
        ...prev,
        duplicationColumns: {
          ...prev.duplicationColumns,
          [entry.internalName]: columns,
        },
      }));
      return columns;
    },
    [fetchColumnsForReport, fetchColumnsForImmediateReport]
  );

  // Completes the pending duplication, replacing the entry being edited when
  // there is one. Returns the entry and the name it previously had, so the
  // caller can rewrite references to a renamed duplicate.
  const completeDuplication = useCallback(
    async (name, preAggregationFilters) => {
      if (!pendingDuplication) return null;
      const {
        parent,
        columns: parentColumns,
        entry: editing,
      } = pendingDuplication;
      const entry = buildDuplicationEntry(
        parent,
        parentColumns,
        name,
        preAggregationFilters
      );
      const columns = await fetchDuplicationColumns(entry);
      setState((prev) => {
        const duplicationColumns = {
          ...prev.duplicationColumns,
          [entry.internalName]: columns,
        };
        if (editing && editing.internalName !== entry.internalName) {
          delete duplicationColumns[editing.internalName];
        }
        return {
          duplications: editing
            ? prev.duplications.map((d) =>
                d.internalName === editing.internalName ? entry : d
              )
            : [...prev.duplications, entry],
          duplicationColumns,
        };
      });
      setPendingDuplication(null);
      return { entry, previousInternalName: editing?.internalName ?? null };
    },
    [pendingDuplication, fetchDuplicationColumns]
  );

  const isDuplicateNameAvailable = useCallback(
    (name) =>
      isNameAvailable(
        name,
        tables,
        savedReports,
        state.duplications.filter(
          (d) => d.internalName !== pendingDuplication?.entry?.internalName
        )
      ),
    [tables, savedReports, state.duplications, pendingDuplication]
  );

  const findDuplication = useCallback(
    (id) => state.duplications.find((d) => d.internalName === id),
    [state.duplications]
  );

  const localReports = useMemo(
    () =>
      Object.fromEntries(
        state.duplications.map((d) => [
          d.internalName,
          {
            report: d,
            columns: state.duplicationColumns[d.internalName] ?? [],
          },
        ])
      ),
    [state]
  );

  const setDuplicationsFromReport = useCallback((existingReport) => {
    if (existingReport?.type !== "MULTI_TABLE_REPORT") {
      setState(EMPTY_STATE);
      return;
    }
    const children = (existingReport.reports ?? []).filter(hasDuplicateOf);
    const duplications = children.map(fromEmbeddedChild);
    const duplicationColumns = Object.fromEntries(
      children.map((child, index) => [
        duplications[index].internalName,
        columnDescriptorsToColumnDefinitions(child.expectedColumns ?? []),
      ])
    );
    setState({ duplications, duplicationColumns });
  }, []);

  return {
    duplications: state.duplications,
    pendingDuplication,
    beginDuplication,
    beginEditDuplication,
    cancelDuplication,
    completeDuplication,
    isDuplicateNameAvailable,
    findDuplication,
    fetchDuplicationColumns,
    localReports,
    setDuplicationsFromReport,
  };
};
